import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';

const TONE = {
  covered: 'text-emerald-700 border-emerald-300 bg-emerald-50',
  partial: 'text-amber-800 border-amber-300 bg-amber-50',
  unmatched: 'text-rose-700 border-rose-300 bg-rose-50',
};
const LABEL = { covered: 'Covered', partial: 'Partial coverage', unmatched: 'Not covered' };

export const CoverageStatusBadge = ({ status, testId }) => (
  <Badge variant="outline" status={TONE[status] ? undefined : 'neutral'} className={`font-normal ${TONE[status] || ''}`} data-testid={testId}>{LABEL[status] || 'Not checked'}</Badge>
);

export const OverrideBanner = ({ override, testId = 'evidence-override-banner' }) => (
  override ? (
    <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={testId}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <div className="font-semibold">Evidence override applied. Audit risk increased.</div>
        <div className="text-xs">{override.reason_label}{override.reason_other ? `: ${override.reason_other}` : ''} · {override.by_name} · {new Date(override.at).toLocaleString('en-GB')}</div>
        <div className="text-xs italic">{override.note}</div>
      </div>
    </div>
  ) : null
);

export const EvidenceCoverageSummary = ({ coverage, compact = false, testPrefix = 'coverage' }) => {
  if (!coverage) return <p className="text-xs text-muted-foreground" data-testid={`${testPrefix}-empty`}>No coverage check yet.</p>;
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-summary`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold" data-testid={`${testPrefix}-covered`}>{coverage.covered_label} line items covered</span>
        <Badge variant="outline" data-testid={`${testPrefix}-partial`}>{coverage.partial} partial</Badge>
        <Badge variant="outline" data-testid={`${testPrefix}-unmatched`}>{coverage.unmatched} not covered</Badge>
        <Badge variant="outline" data-testid={`${testPrefix}-confidence`}>Confidence {coverage.confidence_score}%</Badge>
      </div>
      {coverage.missing?.length > 0 && (
        <ul className="space-y-1 text-xs" data-testid={`${testPrefix}-missing`}>
          {coverage.missing.slice(0, compact ? 5 : 50).map((m) => (
            <li key={m.case_item_id} className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{m.sku}</span>
              <CoverageStatusBadge status={m.status} />
              <span className="text-muted-foreground">{m.reasons.join(' ')}</span>
            </li>
          ))}
        </ul>
      )}
      {coverage.warnings?.length > 0 && (
        <ul className="space-y-1 text-xs text-amber-800" data-testid={`${testPrefix}-warnings`}>
          {coverage.warnings.map((w) => <li key={w} className="flex gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{w}</li>)}
        </ul>
      )}
    </div>
  );
};
